import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

/**
 * Returns whether the current user is an admin (GET /api/admin/check).
 * Only calls the API when signed in to avoid 401/403 console errors.
 * @returns {{ isAdmin: boolean, loading: boolean }}
 */
export function useAdmin() {
  const { isSignedIn, getToken } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const onRefresh = () => setTick((t) => t + 1);
    window.addEventListener('admin-role-changed', onRefresh);
    window.addEventListener('me-role-changed', onRefresh);
    return () => {
      window.removeEventListener('admin-role-changed', onRefresh);
      window.removeEventListener('me-role-changed', onRefresh);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isSignedIn) {
        if (!cancelled) {
          setIsAdmin(false);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      try {
        const res = await api('/api/admin/check', { getToken });
        if (!cancelled) {
          const data = res.ok ? await res.json().catch(() => ({})) : {};
          setIsAdmin(data.isAdmin === true);
        }
      } catch {
        if (!cancelled) setIsAdmin(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn, getToken, tick]);

  return { isAdmin, loading };
}
